"use client";

import React from "react";
import IntroHeader from "@/app/intro/components/IntroHeader";
import IntroLogos from "@/app/intro/components/IntroLogos";
import PlanDivider from "@/app/plans/components/PlanDivider";
import PlanHero from "@/app/plans/components/PlanHero";
import PlanWaitlistForm from "@/app/plans/components/PlanWaitlistForm";

type GsapCore = typeof import("gsap").gsap;

export default function MarathonClient() {
	const headerRef = React.useRef<HTMLDivElement>(null);
	const heroRef = React.useRef<HTMLDivElement>(null);
	const bodyRef = React.useRef<HTMLDivElement>(null);
	const formRef = React.useRef<HTMLDivElement>(null);
	const footerRef = React.useRef<HTMLDivElement>(null);
	const gsapModuleRef = React.useRef<GsapCore | null>(null);
	const timelineRef = React.useRef<ReturnType<GsapCore["timeline"]> | null>(
		null,
	);

	React.useEffect(() => {
		const root = document.documentElement;
		root.dataset.lenisDisabled = "true";
		return () => {
			delete root.dataset.lenisDisabled;
		};
	}, []);

	React.useEffect(() => {
		if (typeof window === "undefined") return;

		let cancelled = false;

		const animate = async () => {
			if (!gsapModuleRef.current) {
				const mod = await import("gsap");
				const instance =
					(mod as unknown as { gsap?: GsapCore }).gsap ??
					(mod as unknown as { default?: GsapCore }).default ??
					(mod as unknown as GsapCore);
				gsapModuleRef.current = instance;
			}

			if (cancelled) return;

			const gsap = gsapModuleRef.current as GsapCore;
			const prefersReducedMotion = window.matchMedia(
				"(prefers-reduced-motion: reduce)",
			).matches;

			if (prefersReducedMotion) return;

			const sections = [
				headerRef.current,
				heroRef.current,
				bodyRef.current,
				formRef.current,
				footerRef.current,
			].filter((node): node is HTMLDivElement => Boolean(node));

			if (!sections.length) return;

			timelineRef.current?.kill();

			gsap.set(sections, {
				opacity: 0,
				y: 24,
				filter: "blur(24px)",
			});

			timelineRef.current = gsap.timeline({
				defaults: { duration: 0.6, ease: "power2.out" },
			});

			timelineRef.current.to(sections, {
				opacity: 1,
				y: 0,
				filter: "blur(0px)",
				stagger: 0.12,
			});
			timelineRef.current.eventCallback("onComplete", () => {
				gsap.set(sections, { clearProps: "transform,filter" });
			});
		};

		void animate();

		return () => {
			cancelled = true;
			timelineRef.current?.kill();
		};
	}, []);

	return (
		<div className="relative flex min-h-screen flex-col overflow-x-clip bg-white">
			{/* Header */}
			<div className="pointer-events-none fixed inset-x-0 top-0 z-40 p-4">
				<div ref={headerRef}>
					<IntroHeader />
				</div>
			</div>

			{/* Top gradient (below nav) */}
			<div className="pointer-events-none fixed inset-x-0 top-0 z-30 h-20 bg-gradient-to-b from-white to-transparent" />
			{/* Bottom gradient */}
			<div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 h-20 bg-gradient-to-t from-white to-transparent" />

			{/* Page Content */}
			<div className="relative z-10 mx-auto flex w-full max-w-[588px] flex-1 flex-col items-center gap-[80px] px-6 pb-[80px] pt-[144px]">
				{/* Hero */}
				<div ref={heroRef} className="flex w-full flex-col items-center gap-10">
					<PlanHero
						title="The Marathon Plan"
						subtitle="An ongoing partnership. You set priorities, we execute continuously."
						availabilityText="Accepting new clients"
					/>

					<PlanDivider />

					{/* Intro text */}
					<div className="flex w-full flex-col">
						<div className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
							<p>
								You don&apos;t need another freelancer to manage. You need a
								partner that&apos;s already in your Slack, shipping every week —
								without you having to explain the same context twice.
							</p>
							<p className="mt-6">
								The Marathon is an ongoing partnership. You set priorities, we
								execute continuously. Multiple workstreams, continuous output.
								Same senior team, same project manager, building momentum over
								time. Stay because the work is good, not because of a contract.
							</p>
						</div>
					</div>
				</div>

				{/* Body */}
				<div
					ref={bodyRef}
					className="flex w-full flex-col items-center gap-[80px]"
				>
					<PlanDivider />

					{/* Trusted by logos */}
					<IntroLogos title="Trusted by industry-leaders" gap="lg" />

					<PlanDivider />

					{/* What you get */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							What you get
						</h2>
						<div className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
							<p>
								Dedicated designer(s) and project manager, focused on your
								product every month. Brand work, product design, marketing
								assets, custom development. all covered under one relationship.
								Design direction is collaborative. we work together to get it
								right.
							</p>
							<p className="mt-6">
								We deliver against milestones, not hours. You set priorities, we
								ship work. No timesheets, no utilization reports. just
								consistent output that compounds over time.
							</p>
						</div>
					</section>

					<PlanDivider />

					{/* What a month looks like */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							What a month looks like
						</h2>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1 border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<p className="font-display text-sm font-semibold leading-[1.5] tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
									Week 1
								</p>
								<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
									You set the priorities. We plan the milestones and start
									executing immediately.
								</p>
							</div>
							<div className="flex flex-col gap-1 border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<p className="font-display text-sm font-semibold leading-[1.5] tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
									Week 2 - 3
								</p>
								<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
									Designs shipped, feedback addressed, next steps clear. Most
									requests land in 1-3 business days.
								</p>
							</div>
							<div className="flex flex-col gap-1 border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
								<p className="font-display text-sm font-semibold leading-[1.5] tracking-[0.28px] text-[rgba(9,10,12,0.88)]">
									Week 4
								</p>
								<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
									Deliverables wrapped, next month scoped. We communicate async
									through Slack, Figma, or wherever your team already works.
								</p>
							</div>
						</div>
					</section>

					<PlanDivider />

					{/* Best for */}
					<section className="flex w-full flex-col gap-6">
						<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
							Best for
						</h2>
						<div className="flex flex-col gap-3">
							{[
								"Continuous product design and iteration",
								"Iterative brand work across touchpoints",
								"Growth experiments and conversion optimization",
								"Marketing assets, decks, and collateral",
								"Product companies that always need design and dev moving",
							].map((text) => (
								<div
									key={text}
									className="flex items-center gap-2 font-display text-base leading-6 tracking-[0.32px]"
								>
									<span className="text-[rgba(9,10,12,0.32)]">→</span>
									<span className="text-[rgba(9,10,12,0.64)]">{text}</span>
								</div>
							))}
						</div>
					</section>

					<PlanDivider />

					{/* Get started */}
					<section ref={formRef} className="flex w-full flex-col gap-10">
						<div className="flex flex-col gap-6">
							<h2 className="font-display text-[18px] font-semibold leading-7 tracking-[0.18px] text-[rgba(9,10,12,0.88)]">
								Get started
							</h2>
							<p className="font-display text-base leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.64)]">
								15 minutes. Tell us what your team needs. we&apos;ll figure out
								if the Marathon is the right fit.
							</p>
						</div>
						<div className="border-l-[1px] border-[rgba(0,0,0,0.08)] py-4 pl-8">
							<div className="flex flex-col gap-6">
								<h3 className="font-display text-base font-semibold leading-6 tracking-[0.32px] text-[rgba(9,10,12,0.88)]">
									Enter waitlist
								</h3>
								<PlanWaitlistForm plan="marathon" />
							</div>
						</div>
					</section>

					<PlanDivider />

					{/* Footer */}
					<div
						ref={footerRef}
						className="flex flex-col items-center gap-0.5 text-center"
					>
						<p className="font-display text-base font-medium leading-6 text-[rgba(9,10,12,0.56)]">
							With love,
						</p>
						<p className="font-display text-base font-semibold leading-6 tracking-[-0.16px] text-[#ff4f1a]">
							Outpace Studios™
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
